import { addDays, type IsoDate } from '@fd/finance-core';

/** The fields of a transaction the search needs (amounts as bigint). */
export type CounterpartEntry = {
  readonly id: string;
  readonly accountId: string;
  readonly bookingDate: IsoDate;
  readonly amountMinor: bigint;
  readonly currency: string;
  readonly kind: string;
  readonly counterpartAccountId: string | null;
  readonly deleted: boolean;
};

/** How far apart the two sides of one bank transfer may be booked. */
const WINDOW_DAYS = 3;
const SETTLED_KINDS = new Set(['SETTLEMENT_PAYMENT', 'TRANSFER', 'BALANCE_ADJUSTMENT']);

/**
 * THE OTHER SIDE OF A SETTLEMENT PAYMENT (the owner, 01.10.2026: „1 transakcja bez kategorii. to spłata rozliczenia
 * od Karoliny do Marcin”). With both people's accounts in the app one bank transfer shows twice: out of the payer's
 * account and into the payee's. Marking the settlement — by hand on Settlements or by AutoSettle — names one of
 * them as the payment; the other stayed an uncategorised income (+£389,12 on Classic 1960 (M)) and counted as money
 * coming in. The other side: on an account none of whose owners own the payment's account, the opposite amount in
 * the same currency, booked within three days, not already a settlement payment, transfer or correction — and only
 * when exactly one entry fits, so two equal transfers in one week are left for the owner.
 */
export function settlementCounterpart<T extends CounterpartEntry>(
  payment: T,
  entries: readonly T[],
  ownersOf: (accountId: string) => readonly string[],
): T | null {
  const payer = new Set(ownersOf(payment.accountId));
  const from = addDays(payment.bookingDate, -WINDOW_DAYS);
  const to = addDays(payment.bookingDate, WINDOW_DAYS);
  const fits = entries.filter((e) => {
    if (e.deleted || e.id === payment.id || e.accountId === payment.accountId || SETTLED_KINDS.has(e.kind)) return false;
    if (e.currency !== payment.currency || e.amountMinor !== -payment.amountMinor) return false;
    if (e.bookingDate < from || e.bookingDate > to) return false;
    const owners = ownersOf(e.accountId);
    return owners.length > 0 && !owners.some((person) => payer.has(person));
  });
  return fits.length === 1 ? fits[0]! : null;
}

/** Settlement payments whose other side is still an ordinary entry: the pairs to mark. */
export function unpairedSettlementSides<T extends CounterpartEntry>(
  entries: readonly T[],
  ownersOf: (accountId: string) => readonly string[],
): { payment: T; counterpart: T }[] {
  return entries.flatMap((payment) => {
    if (payment.deleted || payment.kind !== 'SETTLEMENT_PAYMENT') return [];
    const counterpart = settlementCounterpart(payment, entries, ownersOf);
    return counterpart ? [{ payment, counterpart }] : [];
  });
}
